'use client';

import classNames from 'classnames';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IconType } from 'react-icons';
import { VariantProps } from 'tailwind-variants';
import { FabItem } from './Item';
import { fabStyles } from './styles';

interface IFabProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof fabStyles.mainButton> {
  direction?: 'top' | 'right' | 'left' | 'bottom';
  openMode?: 'hover' | 'click';
  options: {
    disabled?: boolean;
    icon: IconType;
    label: string;
    onClick: () => void;
  }[];
}

export function Fab({
  className,
  direction = 'top',
  openMode,
  options = [],
}: PropsWithChildren<IFabProps>) {
  const [isFabOpen, setIsFabOpen] = useState<boolean>(false);

  const fabContainerRef = useRef<HTMLInputElement>(null);

  const handleCloseFab = useCallback((event: MouseEvent) => {
    const target = event.target as Element;
    if (fabContainerRef.current && !fabContainerRef.current.contains(target)) {
      setIsFabOpen(false);
    }
  }, []);

  const toggleFab = () => {
    setIsFabOpen(prev => !prev);
  };

  useEffect(() => {
    if (isFabOpen) {
      document.addEventListener('mousedown', handleCloseFab);
    }

    return () => {
      document.removeEventListener('mousedown', handleCloseFab);
    };
  }, [isFabOpen, handleCloseFab]);

  return (
    <div
      className={classNames('relative z-10', {
        group: openMode === 'hover',
      })}
      ref={fabContainerRef}
    >
      {/* Main FAB Button */}
      <button
        className={fabStyles.mainButton({
          openMode,
          open: isFabOpen,
          className,
        })}
        onClick={toggleFab}
      >
        <span
          className={classNames('relative transition duration-300 only:-mx-6', {
            'group-hover:rotate-45': openMode === 'hover',
            '': openMode === 'click',
          })}
        >
          {/* Plus Icon */}
          <svg
            aria-label="Plus icon"
            className="w-7 h-7"
            fill="none"
            role="graphics-symbol"
            stroke="currentColor"
            strokeWidth="1.7"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 4.5v15m7.5-7.5h-15"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {/* Secondary Buttons */}
      <div
        className={fabStyles.buttonList({
          openMode,
          open: isFabOpen,
          direction,
          className,
        })}
      >
        <div
          className={fabStyles.buttonListContent({
            direction,
          })}
        >
          {options.map((option, index) => (
            <FabItem
              direction={direction}
              index={index as 0 | 1 | 2 | 3 | 4}
              key={option.label}
              onClick={() => {
                option.onClick();
                toggleFab();
              }}
              open={isFabOpen}
              openMode={openMode}
              option={option}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
